import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useOrg } from '../data/orgs';
import { useEntityList, createEntity, updateEntity } from '../data/entities';
import { captureScreenOutline } from '../utils/screenOutline';

// The Nova turn spine (ported from the foundation's useCopilotTurn): one
// aiSession per user, turns are queued aiMessage docs the agentos worker
// drives, approval is the one client-writable field. Chat and voice read and
// write the SAME docs, so a request spoken to Nova shows up in the panel and
// the panel's plan is what the voice reads back.
//
// Every turn carries `page` (route + query), `surface`, the org / project /
// document / tab under the cursor, and `screen` — the redacted outline of
// what is rendered (utils/screenOutline.js) — so triage knows where the user
// is AND what they are looking at without asking.

export const NOVA_OPEN_EVENT = 'nova:open';
export const NOVA_CLOSE_EVENT = 'nova:close';
export const NOVA_STATE_EVENT = 'nova:state';
const fire = (name, detail) => { try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* SSR / tests */ } };
export const requestNovaOpen = (detail) => fire(NOVA_OPEN_EVENT, detail);
export const requestNovaClose = () => fire(NOVA_CLOSE_EVENT);
export const announceNovaState = (state) => fire(NOVA_STATE_EVENT, state);

// Route → { dealId, docId, tab } so the runtime can scope tools.
export function routeContext(location) {
  const m = location.pathname.match(/^\/deals\/([^/]+)(?:\/docs\/([^/]+))?/);
  const params = new URLSearchParams(location.search);
  return {
    dealId: m ? m[1] : null,
    docId: m && m[2] ? m[2] : null,
    tab: params.get('tab') || (m && !m[2] ? 'dashboard' : null),
  };
}

export default function useNovaTurn({ enabled = false } = {}) {
  const { user: me } = useAuth();
  const { orgId } = useOrg() || {};
  const location = useLocation();
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState('');
  const creatingRef = useRef(false);

  // The read rule is per-user, so the query must carry the same filter.
  const sessionWhere = useMemo(() => (me ? [['createdBy', '==', me.uid]] : null), [me]);
  const { docs: sessions } = useEntityList('aiSession', {}, { orderBy: 'createdAt', dir: 'desc', enabled: enabled && !!me, where: sessionWhere });
  const scope = useMemo(() => ({ aiSession: sessionId }), [sessionId]);
  const msgWhere = useMemo(() => (me ? [['uid', '==', me.uid]] : null), [me]);
  const { docs: messages } = useEntityList('aiMessage', scope, { orderBy: 'createdAt', dir: 'asc', enabled: enabled && !!sessionId, where: msgWhere });

  // Latest session, or lazily create one on first use.
  useEffect(() => {
    if (!enabled || sessionId || sessions === null || !me) return;
    if (sessions.length) {
      setSessionId(sessions[0].id);
      return;
    }
    if (creatingRef.current) return;
    creatingRef.current = true;
    createEntity('aiSession', {}, { title: 'New session', createdBy: me.uid })
      .then(setSessionId)
      .catch((e) => setError(e.message))
      .finally(() => { creatingRef.current = false; });
  }, [enabled, sessionId, sessions, me]);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    if (!me) throw new Error('sign in to talk to Nova');
    const id = await createEntity('aiSession', {}, { title: 'New session', createdBy: me.uid });
    setSessionId(id);
    return id;
  }, [sessionId, me]);

  const newSession = useCallback(async () => {
    const id = await createEntity('aiSession', {}, { title: 'New session', createdBy: me?.uid });
    setSessionId(id);
    return id;
  }, [me]);

  const context = useCallback(() => {
    const screen = captureScreenOutline({ maxChars: 3500 });
    const { dealId, docId, tab } = routeContext(location);
    const guest = /^\/(u|sign|share|connect)\b/.test(location.pathname);
    return {
      page: `${location.pathname}${location.search}`.slice(0, 300),
      surface: guest ? 'guest' : 'app',
      ...(orgId ? { orgId } : {}),
      ...(dealId ? { dealId } : {}),
      ...(docId ? { docId } : {}),
      ...(tab ? { tab } : {}),
      ...(screen ? { screen } : {}),
    };
  }, [location, orgId]);

  const busy = (messages || []).some((m) => ['queued', 'planning', 'executing'].includes(m.status));

  // Queue a user turn. Returns the message id.
  const enqueue = useCallback(async (content) => {
    const text = String(content || '').trim().slice(0, 8000);
    if (!text) throw new Error('empty message');
    const sid = await ensureSession();
    setError('');
    return createEntity('aiMessage', { aiSession: sid }, {
      role: 'user', content: text, uid: me.uid, status: 'queued', ...context(),
    });
  }, [ensureSession, me, context]);

  // Approve/reject a plan — by id, or the newest plan waiting on THIS user.
  const approve = useCallback(async (decision, msgId = null) => {
    const sid = sessionId;
    if (!sid) throw new Error('no Nova session');
    let id = msgId;
    if (!id) {
      const waiting = [...(messages || [])].reverse().find((m) => m.role === 'user' && m.status === 'awaiting_approval' && m.uid === me?.uid);
      if (!waiting) throw new Error('no plan is waiting for your approval');
      id = waiting.id;
    }
    return updateEntity('aiMessage', { aiSession: sid }, id, { approval: decision === 'rejected' ? 'rejected' : 'approved', updatedAt: Date.now() });
  }, [sessionId, messages, me]);

  return { me, orgId, sessionId, scope, sessions, messages, busy, error, setError, enqueue, approve, newSession, ensureSession, context };
}
